import React, { useState, useEffect } from 'react';
import { Checkbox, Chip, CircularProgress, FormControlLabel, ListSubheader, MenuItem, TextField, Typography } from '@material-ui/core';
import clsx from 'clsx';
import axios from 'axios';
import { API_BASE_URL } from '@config';
import { useSelector } from 'react-redux';
import useStyles from 'app/cluster-styles/useStyles';
import { IMAGE_TYPE, IMAGE_MODE } from '../../util/enums';
import SectionCard from './SectionCard';

// Cloud providers get the Ubuntu 26 Remote Desktop defaults (Heidi Desktop on, DCV off).
const CLOUD_PROVIDERS = ['aws', 'azure', 'gcp', 'ibm', 'oracle'];

// Captured/base image names end with the unix-seconds timestamp they were created at
// ("<prefix>-heidi-<seconds>"), so we can derive a date for any image - even base/cloud
// ones that carry no stored createdAt - straight from the name.
const tsFromName = (name) => {
    const m = /-(\d{9,})$/.exec(name || '');
    return m ? parseInt(m[1], 10) * 1000 : 0;
};
const versionMs = (v) => (v && v.createdAt ? new Date(v.createdAt).getTime() : 0) || tsFromName(v && v.name);
const fmtDate = (ms) => (ms ? new Date(ms).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '');

/**
 * Feature: image-customization - standalone, selection-only image picker (IBM).
 * Rendered AFTER the Infrastructure card so the user picks region + credential first,
 * then the image. Two groups: Heidi "Stacks" (by prefix/version) and "Cloud images"
 * (private images discovered in the account via the infrastructure-verification service,
 * pinned by explicit id). No free text -> no typos.
 */
export default function ImageSelectionSection({ form, setField, provider, disableStackFields }) {
    const classes = useStyles();
    const stacks = useSelector(({ stacks }) => stacks.stack.list) || [];

    const providerStacks = stacks.filter(
        (s) => s.info && s.info.state === 'success' && s.provider === provider
    );

    const osType = form.osType || '';
    const serverImageName = form.serverImageName || '';
    const serverImageId = form.serverImageId || '';
    const serverImageType = form.serverImageType || IMAGE_TYPE.HEIDI_STACK;

    // IBM is unified-only: no Image Mode choice. Force it so the deploy uses the unified
    // template/image resolution regardless of any stale default.
    useEffect(() => {
        if (form.imageMode !== IMAGE_MODE.UNIFIED) setField('imageMode', IMAGE_MODE.UNIFIED);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.imageMode]);

    const [cloudImages, setCloudImages] = useState([]);
    const [cloudLoading, setCloudLoading] = useState(false);
    const [versions, setVersions] = useState([]);
    const [versionStackId, setVersionStackId] = useState(null);

    // Cloud images for the selected region/credential (reuses the CLI-based service).
    // No arch filter here: the OS field is gone, so osType is DERIVED from the chosen
    // image rather than driving the list.
    useEffect(() => {
        if (!form.regionId || !form.credentialId) { setCloudImages([]); setCloudLoading(false); return; }
        setCloudLoading(true);
        const params = new URLSearchParams({
            provider, region: form.regionId, credentialId: form.credentialId
        });
        axios.get(`${API_BASE_URL}/api/instance-availability/images?${params.toString()}`)
            .then((r) => setCloudImages((r.data && r.data.images) || []))
            .catch(() => setCloudImages([]))
            .finally(() => setCloudLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [provider, form.regionId, form.credentialId]);

    // Resolve the osType for a cloud image. Prefer the value we stored at capture time
    // (in the stack's imageVersions) over IBM's metadata, which can be stale (a captured
    // 26.04 image still reports 24.04). Falls back to the endpoint's mapped osType.
    const resolveCloudOsType = (img) => {
        for (let s = 0; s < providerStacks.length; s++) {
            const vers = providerStacks[s].imageVersions || [];
            const hit = vers.find((v) => v.imageId === img.id);
            if (hit && hit.osType) return hit.osType;
        }
        return img.osType || '';
    };

    // Captured versions for the stack matching the current image name.
    useEffect(() => {
        const match = providerStacks.find(
            (s) => s.data && s.data.prefix &&
                (serverImageName === s.data.prefix || serverImageName.indexOf(s.data.prefix + '-') === 0)
        );
        if (match && match.id) {
            if (match.id !== versionStackId) {
                setVersionStackId(match.id);
                axios.get(`${API_BASE_URL}/api/stacks/${match.id}/versions`)
                    .then((r) => {
                        const data = r.data || [];
                        setVersions(data);
                        // Pre-select the latest version rather than the generic "Default
                        // (newest)" sentinel. Pin by image id (-> unified_image_id) so the
                        // deploy resolves it; the name stays the stack prefix. Only when the
                        // image is still unpinned, so we never override a choice on an edit.
                        if (data.length && !serverImageId && serverImageName === match.data.prefix) {
                            const newest = data.slice().sort((a, b) => versionMs(b) - versionMs(a))[0];
                            if (newest && newest.imageId) setField('serverImageId', newest.imageId);
                        }
                    })
                    .catch(() => setVersions([]));
            }
        } else if (versionStackId) {
            setVersionStackId(null);
            setVersions([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serverImageName, stacks]);

    const matchedStack = providerStacks.find(
        (s) => s.data && s.data.prefix &&
            (serverImageName === s.data.prefix || serverImageName.indexOf(s.data.prefix + '-') === 0)
    );
    // A matched stack means the image_name is a stack prefix - show the stack as selected
    // even when a specific version is pinned via serverImageId (the version pin keeps the
    // name = prefix). Only fall back to "cloud:<id>" for a cloud image picked directly.
    const imageSelectValue = matchedStack
        ? `stack:${matchedStack.data.prefix}`
        : (serverImageId ? `cloud:${serverImageId}` : '');
    const selectedStack = providerStacks.find((s) => s.id === versionStackId);

    // The captured versions live in imageVersions[] (with a friendly displayName). The
    // stack's original packer-built image is NOT recorded there, so surface it as a
    // "Base" entry: any cloud image that matches the stack prefix and isn't one of the
    // recorded captures. Lets the user roll all the way back to the unmodified image.
    const stackPrefix = selectedStack && selectedStack.data && selectedStack.data.prefix;
    const capturedIds = new Set(versions.map((v) => v.imageId));
    const baseImages = stackPrefix
        ? cloudImages.filter((img) =>
            img && img.name && !capturedIds.has(img.id) &&
            (img.name === stackPrefix || img.name.indexOf(stackPrefix + '-') === 0))
        : [];

    // Versions/base entries pin by image id (-> unified_image_id). "__default__" = newest
    // by prefix (no pin).
    const versionOptionIds = versions.map((v) => v.imageId).concat(baseImages.map((b) => b.id));
    const versionSelectValue = versionOptionIds.indexOf(serverImageId) !== -1 ? serverImageId : '__default__';

    // On a cloud provider, an Ubuntu 26 image defaults the Remote Desktop to Heidi Desktop
    // with DCV off (other OSes are left untouched). Detect from the resolved osType OR the
    // image name/description, because some cloud base images report stale OS metadata (a
    // captured 26.04 image can come back as ubuntu-24-04). This runs here, at selection
    // time, because the Remote Desktop card only mounts in advanced mode -- but osType is
    // defined the moment an image is picked, in either mode.
    const applyUbuntu26RemoteDesktopDefaults = (osType, label) => {
        if (CLOUD_PROVIDERS.indexOf(provider) === -1) return;
        const hay = ((osType || '') + ' ' + (label || '')).toLowerCase();
        const isUbuntu26 = hay.indexOf('ubuntu-26') !== -1 || hay.indexOf('ubuntu 26') !== -1 || /\b26[.-]04\b/.test(hay);
        if (!isUbuntu26) return;
        setField('dcvEnabled', false);
        setField('defaultRemoteProtocol', 'desktop');
    };

    const handleImageSelect = (val) => {
        if (val && val.indexOf('stack:') === 0) {
            const prefix = val.slice(6);
            const stack = providerStacks.find((s) => s.data && s.data.prefix === prefix);
            setField('serverImageId', '');
            setField('serverImageName', prefix);
            // Derive OS from the stack (its data.type is the source of truth).
            const osType = stack && stack.data && stack.data.type;
            if (osType) setField('osType', osType);
            applyUbuntu26RemoteDesktopDefaults(osType, (stack && (stack.displayName || stack.name)) || prefix);
        } else if (val && val.indexOf('cloud:') === 0) {
            const id = val.slice(6);
            const img = cloudImages.find((i) => i.id === id);
            setField('serverImageId', id);
            setField('serverImageName', img ? img.name : id);
            // Derive OS from the image (prefer stored capture-time osType over IBM metadata).
            const osType = img ? resolveCloudOsType(img) : '';
            if (img) setField('osType', osType);
            applyUbuntu26RemoteDesktopDefaults(osType, img ? ((img.name || '') + ' ' + (img.os || '')) : '');
        }
    };

    return (
        <SectionCard icon="layers" title="Image">
            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                <TextField
                    select
                    label="Image"
                    className={clsx(classes.input)}
                    style={{ flex: 1 }}
                    required
                    disabled={disableStackFields}
                    value={imageSelectValue}
                    variant="outlined"
                    // Force the label to float so it doesn't overlap the "Select an image"
                    // placeholder when nothing is selected yet.
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{ displayEmpty: true, renderValue: (v) => {
                        if (!v) return <span style={{ color: '#94a3b8' }}>Select an image</span>;
                        if (v.indexOf('stack:') === 0) return v.slice(6);
                        const img = cloudImages.find((i) => `cloud:${i.id}` === v);
                        return img ? img.name : v.slice(6);
                    } }}
                    onChange={(e) => handleImageSelect(e.target.value)}
                >
                    <ListSubheader>Stacks</ListSubheader>
                    {providerStacks.length > 0
                        ? providerStacks.map((stack) => (
                            <MenuItem key={`stack:${stack.id}`} value={`stack:${stack.data.prefix}`}>
                                <span style={{ flex: 1 }}>
                                    {stack.displayName || stack.name}
                                    <span style={{ color: '#94a3b8', marginLeft: 6 }}>({stack.data.prefix})</span>
                                </span>
                                {stack.data && stack.data.type && (
                                    <Chip
                                        size="small"
                                        className="ml-8"
                                        style={{ background: '#e0f2fe', color: '#075985' }}
                                        label={stack.data.type}
                                    />
                                )}
                            </MenuItem>
                        ))
                        : <MenuItem disabled>No stacks built for this provider</MenuItem>}
                    <ListSubheader>Cloud images</ListSubheader>
                    {cloudLoading
                        ? <MenuItem disabled>
                            <CircularProgress size={16} className="mr-8" />
                            Loading cloud images...
                        </MenuItem>
                        : cloudImages.length > 0
                            ? cloudImages.map((img) => (
                                <MenuItem key={`cloud:${img.id}`} value={`cloud:${img.id}`}>
                                    <span style={{ flex: 1 }}>{img.name}</span>
                                    <Chip
                                        size="small"
                                        className="ml-8"
                                        style={{ background: '#e0f2fe', color: '#075985' }}
                                        label={img.os ? `${img.os}` : (img.arch || 'image')}
                                    />
                                </MenuItem>
                            ))
                            : <MenuItem disabled>{form.regionId && form.credentialId ? 'No cloud images found' : 'Select region + credential first'}</MenuItem>}
                </TextField>

                <FormControlLabel
                    style={{ marginTop: '4px', width: '180px' }}
                    control={
                        <Checkbox
                            color="primary"
                            disabled={disableStackFields}
                            checked={serverImageType === IMAGE_TYPE.CUSTOM_STACK}
                            onChange={(e) => setField('serverImageType', e.target.checked ? IMAGE_TYPE.CUSTOM_STACK : IMAGE_TYPE.HEIDI_STACK)}
                        />
                    }
                    label={
                        <span>
                            Custom image
                            <Typography variant="caption" color="textSecondary" display="block">
                                Leave off for a Heidi stack
                            </Typography>
                        </span>
                    }
                />
            </div>

            {selectedStack && (versions.length > 0 || baseImages.length > 0) && (
                <div style={{ marginTop: '16px' }}>
                    <TextField
                        select
                        label="Image Version"
                        className={clsx(classes.input)}
                        style={{ width: '420px' }}
                        disabled={disableStackFields}
                        value={versionSelectValue}
                        variant="outlined"
                        helperText="Pick an older version to roll back; default uses the newest."
                        onChange={(e) => {
                            const val = e.target.value;
                            // Always keep the image name as the stack prefix; the specific
                            // image is pinned by id (val) so the unified template resolves it.
                            if (selectedStack) setField('serverImageName', selectedStack.data.prefix);
                            setField('serverImageId', val === '__default__' ? '' : val);
                        }}
                    >
                        <MenuItem value="__default__">Default (newest)</MenuItem>
                        {versions.map((v) => {
                            // Captures carry a friendly displayName; the packer base image
                            // (source 'packer') is labelled "Base". A legacy capture with no
                            // name just shows its image name rather than masquerading as Base.
                            const label = v.source === 'packer' ? 'Base' : (v.displayName || '');
                            const date = fmtDate(versionMs(v));
                            return (
                                <MenuItem key={v._id || v.imageId} value={v.imageId}>
                                    <span style={{ flex: 1 }}>
                                        {label ? <strong>{label}</strong> : null}
                                        <span style={{ color: '#94a3b8', marginLeft: label ? 6 : 0 }}>{v.name}</span>
                                    </span>
                                    {date ? <span style={{ color: '#94a3b8', marginLeft: 12, fontSize: 12 }}>{date}</span> : null}
                                    {v.isDefault ? <span style={{ color: '#94a3b8', marginLeft: 6 }}>(default)</span> : null}
                                </MenuItem>
                            );
                        })}
                        {baseImages.map((img) => {
                            const date = fmtDate(tsFromName(img.name));
                            return (
                                <MenuItem key={img.id} value={img.id}>
                                    <span style={{ flex: 1 }}>
                                        Base
                                        <span style={{ color: '#94a3b8', marginLeft: 6 }}>{img.name}</span>
                                    </span>
                                    {date ? <span style={{ color: '#94a3b8', marginLeft: 12, fontSize: 12 }}>{date}</span> : null}
                                </MenuItem>
                            );
                        })}
                    </TextField>
                </div>
            )}

        </SectionCard>
    );
}
